import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Building2, Clock, CheckCircle2, Send, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useMyInvitations, useAcceptInvitation } from "@/hooks/useOrgInvitations";
import { useMyAccessRequests, useSubmitAccessRequest } from "@/hooks/useAccessRequests";

const SmartAccessScreen = () => {
  const { user, signOut } = useAuth();
  const { data: invitations = [], isLoading: invLoading } = useMyInvitations();
  const { data: requests = [], isLoading: reqLoading } = useMyAccessRequests();
  const acceptInvite = useAcceptInvitation();
  const submitRequest = useSubmitAccessRequest();

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({ org_name: "", message: "" });

  const pendingInvitations = invitations.filter((i) => i.status === "pending" && new Date(i.expires_at) > new Date());
  const expiredInvitations = invitations.filter(
    (i) => i.status === "expired" || (i.status === "pending" && new Date(i.expires_at) <= new Date()),
  );
  const pendingRequests = requests.filter((r) => r.status === "pending");

  const isLoading = invLoading || reqLoading;

  const handleSubmitRequest = () => {
    if (!requestForm.org_name.trim()) return;
    submitRequest.mutate(
      { requested_org_name: requestForm.org_name, message: requestForm.message || undefined },
      {
        onSuccess: () => {
          setShowRequestForm(false);
          setRequestForm({ org_name: "", message: "" });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Checking your access...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {pendingInvitations.length > 0 ? "You've Been Invited!" : "Welcome to MemberOS"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {pendingInvitations.length > 0
              ? "You have an organization invitation waiting for you."
              : "You're not connected to an organization yet. To get started, you'll need access to an organization."}
          </p>
        </div>

        {/* STATE A: Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="space-y-3">
            {pendingInvitations.map((inv) => (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-5 rounded-xl border border-primary/20 bg-primary/5 space-y-4"
              >
                <div className="flex items-start gap-3">
                  {inv.organizations?.logo_url ? (
                    <img
                      src={inv.organizations.logo_url}
                      alt={`${inv.organizations?.name || "Organization"} logo`}
                      className="w-10 h-10 rounded-lg object-contain"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground">{inv.organizations?.name || inv.org_id}</p>
                    <p className="text-xs text-muted-foreground">
                      Role:{" "}
                      <Badge variant="secondary" className="text-[10px] h-4 px-1.5 ml-1">
                        {inv.role}
                      </Badge>
                    </p>
                  </div>
                  <Badge className="bg-success/10 text-success border-success/20 text-[10px]">
                    <Mail className="w-3 h-3 mr-1" /> Pending
                  </Badge>
                </div>
                <Button
                  onClick={() => acceptInvite.mutate(inv.id)}
                  disabled={acceptInvite.isPending}
                  className="w-full gap-2"
                >
                  {acceptInvite.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Accept Invitation
                </Button>
              </motion.div>
            ))}
          </div>
        )}

        {/* STATE A.1: Expired Invitations */}
        {expiredInvitations.length > 0 && pendingInvitations.length === 0 && (
          <div className="p-4 rounded-xl border border-warning/20 bg-warning/5 space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-warning" />
              <span className="text-sm font-medium text-foreground">Expired Invitation</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Your invitation to{" "}
              <strong>{expiredInvitations[0].organizations?.name || expiredInvitations[0].org_id}</strong> has expired.
              Please contact your organization administrator to request a new invitation.
            </p>
          </div>
        )}

        {/* STATE B: No invitations — show request form */}
        {pendingInvitations.length === 0 && (
          <>
            {/* Pending requests */}
            {pendingRequests.length > 0 && (
              <div className="p-4 rounded-xl border border-info/20 bg-info/5 space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-info" />
                  <span className="text-sm font-medium text-foreground">Access Request Pending</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Your request to join <strong>{pendingRequests[0].requested_org_name}</strong> is being reviewed.
                  You'll be notified once an administrator responds.
                </p>
              </div>
            )}

            {/* Request access form */}
            {!showRequestForm && pendingRequests.length === 0 && (
              <div className="space-y-3">
                <Button onClick={() => setShowRequestForm(true)} variant="outline" className="w-full gap-2">
                  <Send className="w-4 h-4" />
                  Request Access to an Organization
                </Button>
              </div>
            )}

            {showRequestForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="p-5 rounded-xl border border-border bg-card space-y-4"
              >
                <h3 className="text-sm font-semibold text-foreground">Request Organization Access</h3>
                <Input
                  placeholder="Organization name"
                  value={requestForm.org_name}
                  onChange={(e) => setRequestForm((f) => ({ ...f, org_name: e.target.value }))}
                  className="text-sm"
                />
                <Textarea
                  placeholder="Optional message to the administrator..."
                  value={requestForm.message}
                  onChange={(e) => setRequestForm((f) => ({ ...f, message: e.target.value }))}
                  className="text-sm min-h-[80px]"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSubmitRequest}
                    disabled={!requestForm.org_name.trim() || submitRequest.isPending}
                    className="flex-1 gap-2"
                  >
                    {submitRequest.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4" />
                    )}
                    Submit Request
                  </Button>
                  <Button variant="ghost" onClick={() => setShowRequestForm(false)}>
                    Cancel
                  </Button>
                </div>
              </motion.div>
            )}
          </>
        )}

        {/* User info + sign out */}
        <div className="text-center space-y-3 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Signed in as <span className="font-medium text-foreground">{user?.email}</span>
          </p>
          <Button variant="ghost" size="sm" onClick={signOut} className="text-xs text-muted-foreground">
            Sign out
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default SmartAccessScreen;
